/**
 * CompanySheet — Right-side detail sheet for a company (INT-06, INT-07, INT-08)
 *
 * Sections:
 *   1. Company Details (name, code, status toggle)        — INT-06
 *   2. Integrations (Financial + HR config + test button) — INT-07
 *   3. Users with Access (list + add/remove)              — INT-08
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Building2,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Loader2,
  Plus,
  X,
  UserRound,
  Search,
  PoundSterling,
  Users,
  FolderKanban,
  CalendarDays,
  ChevronDown,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { EmployeeImage } from '@/components/EmployeeImage';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@/components/ui/collapsible';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import * as integrationsService from '@/services/integrationsRedesignService';
import { getExternalInstances, getAccessProjectsCompanies } from '@/services/integrationsApi';
import type { ExternalInstanceDto, AccessProjectsCompanyDto } from '@/services/integrationsApi';
import {
  FINANCIAL_PRODUCT_LABELS,
  HR_PRODUCT_LABELS,
  PROJECT_PRODUCT_LABELS,
  PLANNER_PRODUCT_LABELS,
} from '@/types';
import type {
  CompanyWithIntegrations,
  CompanyUser,
  FinancialProduct,
  HRProduct,
  ProjectProduct,
  PlannerProduct,
  IntegrationConnectionStatus,
} from '@/types';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CompanySheetProps {
  company: CompanyWithIntegrations | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCompanyUpdate: (updated: CompanyWithIntegrations) => void;
  /** When set to 'create', the sheet shows a new-company form instead of the detail tabs */
  mode?: 'view' | 'create';
  onCompanyCreated?: (created: CompanyWithIntegrations) => void;
  /** Used in create mode to check code uniqueness */
  existingCodes?: string[];
  /**
   * When true, the sheet opens in view-only mode:
   * all Save / Edit / Connect / Disconnect / Sync / Test / Remove controls are hidden.
   * Used when the user has viewIntegrations but NOT manageIntegrations.
   */
  readOnly?: boolean;
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

function formatLastSync(lastSyncAt: string | null): string {
  if (!lastSyncAt) return 'Never';
  return new Date(lastSyncAt).toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function ConnectionStatusBadge({ status }: { status: IntegrationConnectionStatus }) {
  switch (status) {
    case 'connected':
      return (
        <Badge variant="outline" className="border-success-500 text-success-700 bg-success-50 gap-1 text-xs">
          <CheckCircle2 className="h-3 w-3" />
          Connected
        </Badge>
      );
    case 'error':
      return (
        <Badge variant="destructive" className="gap-1 text-xs">
          <AlertCircle className="h-3 w-3" />
          Error
        </Badge>
      );
    case 'not_connected':
      return (
        <Badge variant="secondary" className="gap-1 text-xs">
          <XCircle className="h-3 w-3" />
          Not Connected
        </Badge>
      );
    case 'not_configured':
      return (
        <Badge variant="outline" className="text-muted-foreground gap-1 text-xs">
          Not Configured
        </Badge>
      );
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Section 1 — Company Details
// ---------------------------------------------------------------------------

interface CompanyDetailsSectionProps {
  company: CompanyWithIntegrations;
  onUpdate: (updated: CompanyWithIntegrations) => void;
  readOnly?: boolean;
}

function CompanyDetailsSection({ company, onUpdate, readOnly = false }: CompanyDetailsSectionProps) {
  const [name, setName] = useState(company.name);
  const [code, setCode] = useState(company.code);
  const [isActive, setIsActive] = useState(company.status === 'active');
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Sync local state when company changes
  useEffect(() => {
    setName(company.name);
    setCode(company.code);
    setIsActive(company.status === 'active');
    setIsDirty(false);
  }, [company.id]);

  function markDirty() {
    setIsDirty(true);
  }

  async function handleSave() {
    setIsSaving(true);
    try {
      const updated = await integrationsService.updateCompany(company.id, {
        name,
        code: code.toUpperCase(),
        status: isActive ? 'active' : 'inactive',
      });
      if (updated) {
        onUpdate(updated);
        setIsDirty(false);
        toast.success('Company details saved');
      }
    } catch {
      toast.error('Failed to save company details');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {/* Name */}
        <div className="space-y-1.5">
          <Label htmlFor="company-name">Company Name</Label>
          <Input
            id="company-name"
            value={name}
            onChange={(e) => { if (!readOnly) { setName(e.target.value); markDirty(); } }}
            readOnly={readOnly}
            className={readOnly ? 'bg-muted cursor-default' : ''}
          />
        </div>

        {/* Code */}
        <div className="space-y-1.5">
          <Label htmlFor="company-code">Company Code</Label>
          <Input
            id="company-code"
            value={code}
            onChange={(e) => { if (!readOnly) { setCode(e.target.value.toUpperCase()); markDirty(); } }}
            placeholder="e.g. ACME"
            className={`uppercase${readOnly ? ' bg-muted cursor-default' : ''}`}
            readOnly={readOnly}
          />
        </div>

        {/* Status toggle */}
        <div className="flex items-center justify-between py-1">
          <div>
            <Label htmlFor="company-status" className="text-sm font-medium">Active</Label>
            <p className="text-xs text-muted-foreground mt-0.5">
              Inactive companies are hidden from the company switcher
            </p>
          </div>
          <Switch
            id="company-status"
            checked={isActive}
            onCheckedChange={(checked) => { if (!readOnly) { setIsActive(checked); markDirty(); } }}
            disabled={readOnly}
          />
        </div>
      </div>

      {isDirty && !readOnly && (
        <Button size="sm" onClick={handleSave} disabled={isSaving} className="w-full">
          {isSaving && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
          Save Details
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 2 — Integration sub-form (Financial or HR)
// ---------------------------------------------------------------------------

interface IntegrationSubSectionProps {
  companyId: string;
  type: 'financial' | 'hr' | 'project' | 'planner';
  product: FinancialProduct | HRProduct | ProjectProduct | PlannerProduct | null;
  externalInstanceId: string | null;
  externalInstanceIdSecondary?: string | null;
  status: IntegrationConnectionStatus;
  lastSyncAt: string | null;
  onSaved: (updated: CompanyWithIntegrations) => void;
  /**
   * The current financial product already configured for this company.
   * Used by the Project integration to automatically set up Access Financials
   * when Access Financial Operations for Projects is selected.
   */
  currentFinancialProduct?: FinancialProduct | null;
  /** When true, all edit controls (save, test, sync, remove) are hidden. */
  readOnly?: boolean;
}

function IntegrationSubSection({
  companyId,
  type,
  product,
  externalInstanceId,
  externalInstanceIdSecondary,
  status,
  lastSyncAt,
  onSaved,
  currentFinancialProduct,
  readOnly = false,
}: IntegrationSubSectionProps) {
  const [selectedProduct, setSelectedProduct] = useState<string>(product ?? '');
  const [selectedInstanceId, setSelectedInstanceId] = useState<string>(externalInstanceId ?? '');
  const [selectedInstanceName, setSelectedInstanceName] = useState<string>('');
  const [externalInstances, setExternalInstances] = useState<ExternalInstanceDto[]>([]);
  const [isLoadingInstances, setIsLoadingInstances] = useState(false);
  const [noInstancesFound, setNoInstancesFound] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [localLastSyncAt, setLocalLastSyncAt] = useState<string | null>(lastSyncAt);
  const [lastTestSuccess, setLastTestSuccess] = useState<boolean | null>(null);
  const initialLoadDone = useRef(false);

  // Access Projects company selection state
  const [accessProjectsCompanies, setAccessProjectsCompanies] = useState<AccessProjectsCompanyDto[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>(externalInstanceIdSecondary ?? '');
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [noCompaniesFound, setNoCompaniesFound] = useState(false);

  const isAccessProjects = selectedProduct === 'access-projects';

  /** Products that require a Financial Operations GraphQL connection test. */
  const requiresConnectionTest =
    selectedProduct === 'access-financials-operations' ||
    selectedProduct === 'access-resource-scheduler';

  // Load external instances when a product is already selected (on mount / prop change)
  useEffect(() => {
    setSelectedProduct(product ?? '');
    setSelectedInstanceId(externalInstanceId ?? '');
    setSelectedInstanceName('');
    setLocalLastSyncAt(lastSyncAt);
    setIsDirty(false);
    initialLoadDone.current = false;
    setSelectedCompanyId(externalInstanceIdSecondary ?? '');
    setAccessProjectsCompanies([]);
    setNoCompaniesFound(false);

    if (product) {
      loadInstances(product, externalInstanceId ?? undefined);
    } else {
      setExternalInstances([]);
      setNoInstancesFound(false);
    }
  }, [product, externalInstanceId, externalInstanceIdSecondary, lastSyncAt, companyId]);

  async function loadInstances(productCode: string, preselectId?: string) {
    setIsLoadingInstances(true);
    setNoInstancesFound(false);
    setExternalInstances([]);
    try {
      const instances = await getExternalInstances(productCode);
      setExternalInstances(instances);
      const isAP = productCode === 'access-projects';
      if (instances.length === 0) {
        setNoInstancesFound(true);
        setSelectedInstanceId('');
        setSelectedInstanceName('');
      } else if (preselectId) {
        // Try to match by stored external instance ID
        const match = instances.find((i) => i.id === preselectId);
        if (match) {
          setSelectedInstanceName(match.name);
        } else {
          // Fallback: select first instance
          setSelectedInstanceName(instances[0].name);
          setSelectedInstanceId(instances[0].id);
        }
        // For access-projects, load companies using the resolved instance ID
        if (isAP) {
          loadCompanies(match ? preselectId : instances[0].id);
        }
      } else {
        // Auto-select first instance
        setSelectedInstanceName(instances[0].name);
        setSelectedInstanceId(instances[0].id);
        // For access-projects, load companies using the first instance
        if (isAP) {
          loadCompanies(instances[0].id);
        }
      }
    } catch {
      setNoInstancesFound(true);
      setSelectedInstanceId('');
      setSelectedInstanceName('');
    } finally {
      setIsLoadingInstances(false);
      initialLoadDone.current = true;
    }
  }

  async function loadCompanies(instanceId: string) {
    setIsLoadingCompanies(true);
    setNoCompaniesFound(false);
    setAccessProjectsCompanies([]);
    try {
      const companies = await getAccessProjectsCompanies(instanceId);
      setAccessProjectsCompanies(companies);
      if (companies.length === 0) {
        setNoCompaniesFound(true);
      }
    } catch {
      setNoCompaniesFound(true);
    } finally {
      setIsLoadingCompanies(false);
    }
  }

  function handleProductChange(value: string) {
    setSelectedProduct(value);
    setIsDirty(true);
    setSelectedInstanceId('');
    setSelectedInstanceName('');
    setExternalInstances([]);
    setNoInstancesFound(false);
    setAccessProjectsCompanies([]);
    setSelectedCompanyId('');
    setNoCompaniesFound(false);
    if (value) {
      loadInstances(value);
    }
  }

  function handleInstanceChange(instanceName: string) {
    setSelectedInstanceName(instanceName);
    const instance = externalInstances.find((i) => i.name === instanceName);
    setSelectedInstanceId(instance?.id ?? '');
    setIsDirty(true);
  }

  const productOptions =
    type === 'financial'
      ? (Object.entries(FINANCIAL_PRODUCT_LABELS) as [FinancialProduct, string][])
      : type === 'hr'
        ? (Object.entries(HR_PRODUCT_LABELS) as [HRProduct, string][])
        : type === 'project'
          ? (Object.entries(PROJECT_PRODUCT_LABELS) as [ProjectProduct, string][])
          : (Object.entries(PLANNER_PRODUCT_LABELS) as [PlannerProduct, string][]);

  async function handleSave() {
    setIsSaving(true);
    try {
      const emptyField = { product: null, externalInstanceId: null };
      const activeField = {
        product: (selectedProduct || null) as FinancialProduct | HRProduct | ProjectProduct | PlannerProduct | null,
        externalInstanceId: selectedInstanceId || null,
        instanceName: selectedInstanceName || null,
        ...(type === 'project' && isAccessProjects ? { externalInstanceIdSecondary: selectedCompanyId || null } : {}),
      };
      const payload: import('@/types').UpdateIntegrationsPayload =
        type === 'financial'
          ? { financial: activeField as typeof payload.financial, hr: emptyField, project: emptyField, planner: emptyField }
          : type === 'hr'
          ? { financial: emptyField, hr: activeField as typeof payload.hr, project: emptyField, planner: emptyField }
          : type === 'project'
          ? { financial: emptyField, hr: emptyField, project: activeField as typeof payload.project, planner: emptyField }
          : { financial: emptyField, hr: emptyField, project: emptyField, planner: activeField as typeof payload.planner };
      const updated = await integrationsService.updateIntegrations(companyId, payload);
      if (updated) {
        // ── Auto-link rule ──────────────────────────────────────────────────────
        // If the user configures "Access Financial Operations for Projects" on the
        // Project integration AND no Financial integration is set yet, automatically
        // create the linked "Access Financials" financial integration.
        // (The reverse is NOT true — having only Access Financials is fine.)
        if (
          type === 'project' &&
          selectedProduct === 'access-financials-operations' &&
          !currentFinancialProduct
        ) {
          try {
            // Use the same external instance selected for the project integration
            // as a sensible default for Access Financials (same tenant).
            const financialPayload: import('@/types').UpdateIntegrationsPayload = {
              financial: {
                product: 'access-financials' as FinancialProduct,
                externalInstanceId: selectedInstanceId || null,
                instanceName: selectedInstanceName || null,
              },
              hr: emptyField,
              project: emptyField,
              planner: emptyField,
            };
            const withFinancial = await integrationsService.updateIntegrations(
              companyId,
              financialPayload
            );
            if (withFinancial) {
              onSaved(withFinancial);
              setIsDirty(false);
              toast.success('Project integration saved', {
                description:
                  'Access Financials integration was automatically added — it is required by Access Financial Operations for Projects.',
              });
              return;
            }
          } catch {
            // Non-fatal: the project integration already saved; just warn.
            toast.warning('Project integration saved', {
              description:
                'Could not automatically create the linked Access Financials integration. Please configure it manually.',
            });
            onSaved(updated);
            setIsDirty(false);
            return;
          }
        }
        // ───────────────────────────────────────────────────────────────────────

        onSaved(updated);
        setIsDirty(false);

        const typeLabel = type === 'financial' ? 'Financial' : type === 'hr' ? 'HR' : type === 'project' ? 'Project' : 'Planner';

        // Auto-run connection test after save for products that require it.
        if (requiresConnectionTest) {
          toast.success(`${typeLabel} integration saved — testing connection…`);
          const testPassed = await handleTest();

          // When a planner auto-sync product is configured for the first time,
          // only trigger an immediate sync if the connection test passed.
          const isNewAutoSyncPlanner =
            type === 'planner' &&
            selectedProduct === 'access-resource-scheduler' &&
            !product;

          if (isNewAutoSyncPlanner && testPassed) {
            handleSync();
          }
        } else {
          // When a planner auto-sync product (e.g. access-resource-scheduler) is
          // configured for the first time, kick off an immediate sync so data is
          // available right away.
          const isNewAutoSyncPlanner =
            type === 'planner' &&
            selectedProduct === 'access-resource-scheduler' &&
            !product;

          if (isNewAutoSyncPlanner) {
            toast.success('Planner integration saved — syncing now…');
            handleSync();
          } else {
            toast.success(`${typeLabel} integration saved`);
          }
        }
      }
    } catch {
      toast.error('Failed to save integration');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleTest(): Promise<boolean> {
    setIsTesting(true);
    try {
      const result = await integrationsService.testConnection(companyId, type);
      setLastTestSuccess(result.success);
      if (result.success) {
        toast.success('Connection successful', { description: result.message });
      } else {
        toast.error('Connection failed', { description: result.message });
      }
      // Refresh company data so the UI picks up the updated connection status
      // and AutoSync flag set by the backend.
      try {
        const refreshed = await integrationsService.getCompany(companyId);
        if (refreshed) onSaved(refreshed);
      } catch { /* non-fatal */ }
      return result.success;
    } catch {
      toast.error('Test connection failed');
      setLastTestSuccess(false);
      return false;
    } finally {
      setIsTesting(false);
    }
  }

  async function handleSync() {
    setIsSyncing(true);
    try {
      const result = await integrationsService.syncIntegration(companyId, type);
      if (result.success) {
        if (result.syncedAt) setLocalLastSyncAt(result.syncedAt);
        if (result.updatedCompany) onSaved(result.updatedCompany);
        toast.success('Sync complete', { description: result.message });
      } else {
        toast.error('Sync failed', { description: result.message });
      }
    } catch {
      toast.error('Sync failed — please try again');
    } finally {
      setIsSyncing(false);
    }
  }

  async function handleRemove() {
    setIsRemoving(true);
    try {
      const updated = await integrationsService.removeIntegration(companyId, type);
      if (updated) {
        onSaved(updated);
        setSelectedProduct('');
        setSelectedInstanceId('');
        setSelectedInstanceName('');
        setExternalInstances([]);
        setLocalLastSyncAt(null);
        setIsDirty(false);
        toast.success(
          `${type === 'financial' ? 'Financial' : type === 'hr' ? 'HR' : type === 'project' ? 'Project' : 'Planner'} integration removed`
        );
      }
    } catch {
      toast.error('Failed to remove integration');
    } finally {
      setIsRemoving(false);
      setShowRemoveConfirm(false);
    }
  }

  const label =
    type === 'financial'
      ? 'Financial Integration'
      : type === 'hr'
        ? 'HR Integration'
        : type === 'project'
          ? 'Project Integration'
          : 'Planner Integration';

  const icon =
    type === 'financial' ? (
      <PoundSterling className="h-4 w-4 text-muted-foreground flex-shrink-0" />
    ) : type === 'hr' ? (
      <Users className="h-4 w-4 text-muted-foreground flex-shrink-0" />
    ) : type === 'project' ? (
      <FolderKanban className="h-4 w-4 text-muted-foreground flex-shrink-0" />
    ) : (
      <CalendarDays className="h-4 w-4 text-muted-foreground flex-shrink-0" />
    );

  // Default open if already configured; closed if never set up
  const defaultOpen = status !== 'not_configured';

  return (
    <>
    <Collapsible defaultOpen={defaultOpen}>
      {/* ── Trigger row ── */}
      <div className="flex items-center gap-1 rounded-md hover:bg-muted/50 transition-colors group">
        <CollapsibleTrigger className="flex-1 flex items-center gap-2 px-1 py-1.5 min-w-0">
          {icon}
          <span className="text-sm font-semibold flex-1 text-left truncate">{label}</span>
          <ConnectionStatusBadge status={status} />
          <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180 flex-shrink-0 ml-1" />
        </CollapsibleTrigger>
        {status !== 'not_configured' && !readOnly && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 flex-shrink-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            onClick={(e) => { e.stopPropagation(); setShowRemoveConfirm(true); }}
            disabled={isRemoving}
            title="Remove integration"
          >
            {isRemoving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
          </Button>
        )}
      </div>

      {/* ── Collapsible content ── */}
      <CollapsibleContent>
        <div className="space-y-2 pl-6 pt-3">
          {/* Product dropdown */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Product</Label>
            {readOnly ? (
              <p className="text-sm py-1">
                {selectedProduct
                  ? (productOptions.find(([v]) => v === selectedProduct)?.[1] ?? selectedProduct)
                  : <span className="text-muted-foreground italic">Not configured</span>}
              </p>
            ) : (
              <Select
                value={selectedProduct}
                onValueChange={handleProductChange}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Select product..." />
                </SelectTrigger>
                <SelectContent>
                  {productOptions.map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Instance Name — hidden for access-projects (replaced by Company selector) */}
          {selectedProduct && !isAccessProjects && (
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Instance Name</Label>
            {readOnly ? (
              <p className="text-sm py-1">
                {selectedInstanceName
                  ? selectedInstanceName
                  : <span className="text-muted-foreground italic">—</span>}
              </p>
            ) : isLoadingInstances ? (
              <div className="flex items-center gap-2 p-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading instances...
              </div>
            ) : noInstancesFound ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  No Instances Found. Unable to configure this product.
                </AlertDescription>
              </Alert>
            ) : externalInstances.length > 0 ? (
              <>
                <Select value={selectedInstanceName} onValueChange={handleInstanceChange}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Select an instance name" />
                  </SelectTrigger>
                  <SelectContent>
                    {externalInstances.map((instance) => (
                      <SelectItem key={instance.id} value={instance.name}>
                        {instance.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Select an external instance for this product
                </p>
              </>
            ) : null}
          </div>
          )}

          {/* Company selector — shown only for access-projects */}
          {selectedProduct && isAccessProjects && (
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Company</Label>
            {readOnly ? (
              <p className="text-sm py-1">
                {selectedCompanyId
                  ? (accessProjectsCompanies.find((c) => c.id === selectedCompanyId)?.name ?? selectedCompanyId)
                  : <span className="text-muted-foreground italic">—</span>}
              </p>
            ) : isLoadingInstances || isLoadingCompanies ? (
              <div className="flex items-center gap-2 p-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading companies...
              </div>
            ) : noInstancesFound ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  No Instances Found. Unable to configure this product.
                </AlertDescription>
              </Alert>
            ) : noCompaniesFound ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  No Companies Found. Unable to configure this product.
                </AlertDescription>
              </Alert>
            ) : accessProjectsCompanies.length > 0 ? (
              <>
                <Select
                  value={selectedCompanyId}
                  onValueChange={(value) => {
                    setSelectedCompanyId(value);
                    setIsDirty(true);
                  }}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Select a company" />
                  </SelectTrigger>
                  <SelectContent>
                    {accessProjectsCompanies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Select a company from Access Projects
                </p>
              </>
            ) : null}
          </div>
          )}

          {/* Last sync — detailed row inside expanded area */}
          {localLastSyncAt && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <RefreshCw className="h-3 w-3 flex-shrink-0" />
              <span>Last synced: <span className="font-medium text-foreground">{formatLastSync(localLastSyncAt)}</span></span>
            </div>
          )}

          {/* Actions — hidden in read-only mode */}
          {!readOnly && <div className="flex flex-wrap gap-2 pt-1 pb-2">
            {/* Sync Now — only when connected and (for products requiring connection test) when test passed */}
            {status === 'connected' && (!requiresConnectionTest || lastTestSuccess !== false) && (
              <Button
                variant="outline"
                size="sm"
                className="text-xs h-7"
                onClick={handleSync}
                disabled={isSyncing}
              >
                {isSyncing ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <RefreshCw className="h-3 w-3 mr-1" />
                )}
                {isSyncing ? 'Syncing…' : 'Sync Now'}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-7"
              onClick={handleTest}
              disabled={isTesting || !selectedProduct || !selectedInstanceId}
            >
              {isTesting ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <CheckCircle2 className="h-3 w-3 mr-1" />
              )}
              Test Connection
            </Button>
            <Button
              size="sm"
              className="text-xs h-7"
              onClick={handleSave}
              disabled={isSaving || !isDirty}
            >
              {isSaving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              Save
            </Button>
          </div>}
        </div>
      </CollapsibleContent>
    </Collapsible>

    {/* ── Remove Integration confirmation ── */}
    <AlertDialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove {label}?</AlertDialogTitle>
          <AlertDialogDescription>
            This will disconnect and remove all configuration for the {label.toLowerCase()}.
            Any active sync schedule will be cancelled. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isRemoving}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleRemove}
            disabled={isRemoving}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isRemoving ? (
              <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Removing…</>
            ) : (
              'Remove Integration'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Section 3 — Users with Access
// ---------------------------------------------------------------------------

interface UsersWithAccessSectionProps {
  company: CompanyWithIntegrations;
  onUserCountChange: (count: number) => void;
}

function UsersWithAccessSection({ company, onUserCountChange }: UsersWithAccessSectionProps) {
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Inline filter — searches within the already-loaded user list
  const [filterQuery, setFilterQuery] = useState('');
  // Add-user popover search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<CompanyUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isAdding, setIsAdding] = useState<string | null>(null);
  const [removingUser, setRemovingUser] = useState<CompanyUser | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await integrationsService.getCompanyUsers(company.id);
      setUsers(data);
      onUserCountChange(data.length);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, [company.id]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  async function handleSearch(query: string) {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const results = await integrationsService.searchUsers(query, company.id);
      setSearchResults(results);
    } catch {
      toast.error('Search failed');
    } finally {
      setIsSearching(false);
    }
  }

  async function handleAddUser(user: CompanyUser) {
    setIsAdding(user.id);
    try {
      await integrationsService.addUserToCompany(company.id, user.id);
      await loadUsers();
      setSearchQuery('');
      setSearchResults([]);
      setIsAddOpen(false);
      toast.success(`${user.name} added to ${company.name}`);
    } catch {
      toast.error('Failed to add user');
    } finally {
      setIsAdding(null);
    }
  }

  async function handleRemoveUser() {
    if (!removingUser) return;
    setIsRemoving(true);
    try {
      await integrationsService.removeUserFromCompany(company.id, removingUser.id);
      await loadUsers();
      toast.success(`${removingUser.name} removed from ${company.name}`);
      setRemovingUser(null);
    } catch {
      toast.error('Failed to remove user');
    } finally {
      setIsRemoving(false);
    }
  }

  // Derive filtered list from the already-loaded users based on the inline search bar
  const filteredUsers = filterQuery.trim()
    ? users.filter(
        (u) =>
          u.name.toLowerCase().includes(filterQuery.toLowerCase()) ||
          u.role.toLowerCase().includes(filterQuery.toLowerCase())
      )
    : users;

  return (
    <div className="space-y-3">
      {/* Toolbar: search bar + Add User button */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            className="pl-8 h-8 text-sm"
            placeholder="Search users…"
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
          />
        </div>

        {/* Add User popover */}
        <Popover open={isAddOpen} onOpenChange={setIsAddOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1 shrink-0">
              <Plus className="h-3 w-3" />
              Add User
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-2" align="end">
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground px-1 pb-1">
                Add user to {company.name}
              </p>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  className="pl-8 h-8 text-sm"
                  placeholder="Search by name, email, role..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  autoFocus
                />
              </div>

              {isSearching && (
                <div className="flex items-center justify-center py-3">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}

              {!isSearching && searchResults.length > 0 && (
                <div className="max-h-48 overflow-y-auto space-y-0.5">
                  {searchResults.map((user) => (
                    <button
                      key={user.id}
                      className="w-full flex items-center gap-2.5 p-2 rounded-md hover:bg-muted text-left text-sm"
                      onClick={() => handleAddUser(user)}
                      disabled={isAdding === user.id}
                    >
                      <EmployeeImage
                        name={user.name}
                        photoUrl={user.photoUrl}
                        size="sm"
                        className="h-7 w-7 flex-shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{user.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{user.role}</div>
                      </div>
                      {isAdding === user.id && (
                        <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              )}

              {!isSearching && searchQuery && searchResults.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-3">
                  No users found
                </p>
              )}

              {!searchQuery && (
                <p className="text-xs text-muted-foreground text-center py-2">
                  Start typing to search users
                </p>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Count summary */}
      {!isLoading && (
        <p className="text-xs text-muted-foreground">
          {filterQuery.trim()
            ? `${filteredUsers.length} of ${users.length} user${users.length !== 1 ? 's' : ''} match`
            : `${users.length} user${users.length !== 1 ? 's' : ''} with access`}
        </p>
      )}

      {/* User list */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-2 py-1">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="space-y-1 flex-1">
                <Skeleton className="h-3 w-28" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
          ))}
        </div>
      ) : users.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center gap-2">
          <UserRound className="h-8 w-8 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No users assigned yet.</p>
          <p className="text-xs text-muted-foreground">
            Use "Add User" to grant access to this company.
          </p>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center gap-1">
          <Search className="h-6 w-6 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No users match "{filterQuery}"</p>
        </div>
      ) : (
        <div className="space-y-0.5">
          {filteredUsers.map((user) => (
            <div
              key={user.id}
              className="flex items-center gap-2.5 py-2 px-2 rounded-md group hover:bg-muted/50 transition-colors"
            >
              <EmployeeImage
                name={user.name}
                photoUrl={user.photoUrl}
                size="sm"
                className="flex-shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium leading-tight truncate">{user.name}</div>
                <div className="text-xs text-muted-foreground truncate">{user.role}</div>
              </div>
              <button
                className={cn(
                  'opacity-0 group-hover:opacity-100 transition-opacity',
                  'h-7 w-7 flex items-center justify-center rounded-md',
                  'text-muted-foreground hover:text-destructive hover:bg-destructive/10'
                )}
                onClick={() => setRemovingUser(user)}
                title={`Remove ${user.name}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Read-only note */}
      <p className="text-xs text-muted-foreground pt-2 border-t">
        To manage a user's full company access, go to{' '}
        <span className="font-medium">Team Members</span>.
      </p>

      {/* Remove confirmation dialog */}
      <AlertDialog open={!!removingUser} onOpenChange={(o) => { if (!o) setRemovingUser(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove access?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{removingUser?.name}</strong> will lose access to{' '}
              <strong>{company.name}</strong>. They will no longer see this company in their
              switcher.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveUser}
              disabled={isRemoving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isRemoving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Remove Access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create Company Form (shown inside the sheet when mode === 'create')
// ---------------------------------------------------------------------------

interface CreateCompanyFormProps {
  existingCodes: string[];
  onCreated: (company: CompanyWithIntegrations) => void;
  onCancel: () => void;
}

function CreateCompanyForm({ existingCodes, onCreated, onCancel }: CreateCompanyFormProps) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive'>('active');
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; code?: string }>({});

  function validate() {
    const e: { name?: string; code?: string } = {};
    if (!name.trim() || name.trim().length < 2) e.name = 'Company name must be at least 2 characters';
    if (!code.trim() || code.trim().length < 2) e.code = 'Company code must be at least 2 characters';
    else if (code.trim().length > 20) e.code = 'Company code must be at most 20 characters';
    else if (!/^[A-Z0-9-]+$/.test(code.trim())) e.code = 'Uppercase letters, numbers, and hyphens only';
    else if (existingCodes.includes(code.trim().toUpperCase())) e.code = 'This company code is already in use';
    return e;
  }

  async function handleSave() {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setIsSaving(true);
    try {
      const created = await integrationsService.createCompany({ name: name.trim(), code: code.trim().toUpperCase(), status });
      toast.success(`${created.name} created`);
      onCreated(created);
    } catch {
      toast.error('Failed to create company');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="px-6 py-5 space-y-5">
      {/* Name */}
      <div className="space-y-1.5">
        <Label htmlFor="new-company-name">Company Name <span className="text-destructive">*</span></Label>
        <Input
          id="new-company-name"
          value={name}
          placeholder="e.g., UK Operations"
          onChange={(e) => { setName(e.target.value); setErrors((prev) => ({ ...prev, name: undefined })); }}
          disabled={isSaving}
        />
        {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
      </div>

      {/* Code */}
      <div className="space-y-1.5">
        <Label htmlFor="new-company-code">Company Code <span className="text-destructive">*</span></Label>
        <Input
          id="new-company-code"
          value={code}
          placeholder="e.g., UK-OPS"
          className="uppercase"
          onChange={(e) => { setCode(e.target.value.toUpperCase()); setErrors((prev) => ({ ...prev, code: undefined })); }}
          disabled={isSaving}
        />
        <p className="text-xs text-muted-foreground">Unique identifier — uppercase letters, numbers and hyphens only</p>
        {errors.code && <p className="text-xs text-destructive">{errors.code}</p>}
      </div>

      {/* Status */}
      <div className="flex items-center justify-between py-1">
        <div>
          <Label htmlFor="new-company-status" className="text-sm font-medium">Active</Label>
          <p className="text-xs text-muted-foreground mt-0.5">Inactive companies are hidden from the company switcher</p>
        </div>
        <Switch
          id="new-company-status"
          checked={status === 'active'}
          onCheckedChange={(checked) => setStatus(checked ? 'active' : 'inactive')}
          disabled={isSaving}
        />
      </div>

      <Separator />

      {/* Actions */}
      <div className="flex gap-2 pt-1">
        <Button variant="outline" className="flex-1" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button className="flex-1" onClick={handleSave} disabled={isSaving}>
          {isSaving && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
          Create Company
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main CompanySheet
// ---------------------------------------------------------------------------

export function CompanySheet({
  company,
  open,
  onOpenChange,
  onCompanyUpdate,
  mode = 'view',
  onCompanyCreated,
  existingCodes = [],
  readOnly = false,
}: CompanySheetProps) {
  function handleUserCountChange(count: number) {
    if (!company) return;
    onCompanyUpdate({ ...company, userCount: count });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl p-0 flex flex-col overflow-hidden">
        <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted">
              <Building2 className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="min-w-0">
              <SheetTitle className="text-base leading-tight truncate">
                {mode === 'create' ? 'Add New Company' : (company?.name ?? 'Company Details')}
              </SheetTitle>
              <SheetDescription className="text-xs">
                {mode === 'create' ? 'Fill in the details below to create a new company' : company?.code}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {mode === 'create' ? (
          <CreateCompanyForm
            existingCodes={existingCodes}
            onCreated={(created) => {
              onCompanyCreated?.(created);
              onOpenChange(false);
            }}
            onCancel={() => onOpenChange(false)}
          />
        ) : !company ? null : (
          <Tabs defaultValue="details" className="flex flex-col flex-1 min-h-0">
            {/* ── Tab bar — sits just below the sheet header ── */}
            <div className="px-6 pt-3 pb-0 border-b shrink-0">
              <TabsList className="h-9 w-full rounded-none bg-transparent p-0 gap-0 justify-start">
                <TabsTrigger
                  value="details"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 h-9 text-sm"
                >
                  Details & Integrations
                </TabsTrigger>
                <TabsTrigger
                  value="users"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 h-9 text-sm"
                >
                  Users with Access
                  {company.userCount > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-muted text-muted-foreground text-[10px] font-medium h-4 min-w-4 px-1">
                      {company.userCount}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>
            </div>

            {/* ── Tab 1: Details & Integrations ── */}
            <TabsContent value="details" className="flex-1 overflow-y-auto mt-0">
              <div className="px-6 py-5 space-y-6">

                {/* Section 1 — Company Details */}
                <section>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    Company Details
                  </h3>
                  <CompanyDetailsSection
                    company={company}
                    onUpdate={onCompanyUpdate}
                    readOnly={readOnly}
                  />
                </section>

                <Separator />

                {/* Section 2 — Integrations */}
                <section className="pb-6">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    Integrations
                  </h3>
                  <div className="space-y-1">
                    <IntegrationSubSection
                      companyId={company.id}
                      type="financial"
                      product={company.financialIntegration.product}
                      externalInstanceId={company.financialIntegration.externalInstanceId}
                      status={company.financialIntegration.status}
                      lastSyncAt={company.financialIntegration.lastSyncAt}
                      onSaved={onCompanyUpdate}
                      readOnly={readOnly}
                    />
                    <IntegrationSubSection
                      companyId={company.id}
                      type="hr"
                      product={company.hrIntegration.product}
                      externalInstanceId={company.hrIntegration.externalInstanceId}
                      status={company.hrIntegration.status}
                      lastSyncAt={company.hrIntegration.lastSyncAt}
                      onSaved={onCompanyUpdate}
                      readOnly={readOnly}
                    />
                    <IntegrationSubSection
                      companyId={company.id}
                      type="project"
                      product={company.projectIntegration.product}
                      externalInstanceId={company.projectIntegration.externalInstanceId}
                      externalInstanceIdSecondary={company.projectIntegration.externalInstanceIdSecondary}
                      status={company.projectIntegration.status}
                      lastSyncAt={company.projectIntegration.lastSyncAt}
                      onSaved={onCompanyUpdate}
                      currentFinancialProduct={company.financialIntegration.product as FinancialProduct | null}
                      readOnly={readOnly}
                    />
                    <IntegrationSubSection
                      companyId={company.id}
                      type="planner"
                      product={company.plannerIntegration.product}
                      externalInstanceId={company.plannerIntegration.externalInstanceId}
                      status={company.plannerIntegration.status}
                      lastSyncAt={company.plannerIntegration.lastSyncAt}
                      onSaved={onCompanyUpdate}
                      readOnly={readOnly}
                    />
                  </div>
                </section>

              </div>
            </TabsContent>

            {/* ── Tab 2: Users with Access ── */}
            <TabsContent value="users" className="flex-1 overflow-y-auto mt-0">
              <div className="px-6 py-5 pb-8">
                <UsersWithAccessSection
                  company={company}
                  onUserCountChange={handleUserCountChange}
                />
              </div>
            </TabsContent>
          </Tabs>
        )}
      </SheetContent>
    </Sheet>
  );
}

